import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Link,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import cx from 'classnames';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import Delayed from '~/src/components/Delayed';
import LoaderOverlay from '~/src/components/LoaderOverlay';
import {
  customerLinks,
  customerLogos,
  loginProvidersLogos,
} from '../constants';
import useClearEffect from '../hooks/useClearEffect';
import useLogin from '../hooks/useLogin';
import useSocialLogin from '../hooks/useSocialLogin';
import {
  emailState,
  emailValidSelector,
  errorKeySelector,
  isLoadingState,
  isUnconfirmedAccountSelector,
  loginConfigState,
  passwordState,
} from '../store';
import Layout from './NewLayout';

const useStyles = makeStyles(({ spacing, palette }) => ({
  container: {
    overflow: 'hidden',
    '& .orText': {
      display: 'flex',
      justifyContent: 'center',
      color: '#8f8a80',
      lineHeight: '1.6',
    },
    '& .signInTitle': {
      fontWeight: 600,
      fontSize: '22px',
      lineHeight: '1.6',
      margin: '0 0 12px 0',
    },
    '& .forgotpasswordLink': {
      fontSize: '12px',
      color: '#8F8A80',
      lineHeight: '1.6',
      textDecoration: 'underline',
    },
    '& .signInButton': {
      background: '#000000',
      color: '#FFFFFF',
      borderColor: '#000000',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '.5rem',
      padding: '12px 32px',
      borderRadius: '25px',
      lineHeight: '1.6',
      fontWeight: 600,
      fontSize: '16px',
      border: '2px solid transparent',
      '&.Mui-disabled': {
        color: '#a6a6a6',
        borderColor: 'rgba(0, 0, 0, 0.12)',
        backgroundColor: 'rgba(0, 0, 0, 0.12)',
      },
    },
    '& .provider-signin-container': {
      gap: '12px',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  formControlContainer: {
    '& label': {
      padding: 0,
      fontWeight: 600,
      fontSize: '13px',
      lineHeight: '1.6',
      marginBottom: '6px',
      color: 'var(--black)',
    },
    '& .MuiFilledInput-root': {
      background: 'none',
      borderRadius: '0',
    },
    '& input': {
      width: '100%',
      border: '1.5px solid #aba69c',
      borderRadius: '10px !important',
      fontSize: '15px',
    },
  },
  withLogo: {
    paddingTop: spacing(18),
  },
  signUpLink: {
    color: palette.secondary.main,
    fontWeight: 600,
    lineHeight: 1.5,
    marginLeft: '5px',
  },
  confirmAccountLink: {
    color: palette.error.main,
    fontWeight: 600,
    lineHeight: 1.5,
  },
  providersLogo: {
    height: '1.5rem',
    marginRight: '10px',
  },
  providerSignIn: {
    background: '#FFFFFF',
    color: '#000000',
    border: '1.5px solid #000000',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '.5rem',
    padding: '12px 32px',
    borderRadius: '25px',
    fontWeight: 600,
    fontSize: '16px',
    '&:hover': { background: '#FFFFFF' },
  },
  customerLogoClassName: {
    display: 'block',
    marginBottom: spacing(3),
    textAlign: 'center',
    '& img': {
      height: 'auto',
      maxWidth: '50%',
    },
  },
}));

const Login = () => {
  const {
    container,
    formControlContainer,
    confirmAccountLink,
    providersLogo,
    providerSignIn,
    customerLogoClassName,
    withLogo,
  } = useStyles();
  const { t } = useTranslation('auth');
  const [login] = useLogin();
  const socialLogin = useSocialLogin();
  const [user, setUser] = useRecoilState(emailState);
  const [pass, setPass] = useRecoilState(passwordState);
  const userValid = useRecoilValue(emailValidSelector);
  const loginPending = useRecoilValue(isLoadingState);
  const errorKey = useRecoilValue(errorKeySelector);
  const isUnconfirmedAccount = useRecoilValue(isUnconfirmedAccountSelector);
  const [userTouched, setUserTouched] = useState(false);
  const [passTouched, setPassTouched] = useState(false);
  const canLogin = useMemo(
    () => user && pass && userValid,
    [user, pass, userValid],
  );
  const { allowLoginPassword, providers, customerLogo, customer } = useRecoilValue(loginConfigState);

  useClearEffect();

  const handleUserChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserTouched(true);
    setUser(e.target.value);
  };

  const handlePassChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassTouched(true);
    setPass(e.target.value);
  };

  const handleEnterKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter' && canLogin) {
      login();
    }
  };

  return (
    <Layout
      chipText={t('signIn.layoutContainer.title')}
      description={t('signIn.layoutContainer.description')}
      cursorAfterText={t('signIn.layoutContainer.cursorAfterText')}
      cursorBeforeText={t('signIn.layoutContainer.cursorBeforeText')}
      buttonArray={[
        { label: t('signIn.layoutContainer.buttonText'), link: '/signup' },
      ]}
    >
      {loginPending && (
        <Delayed>
          <LoaderOverlay />
        </Delayed>
      )}
      <Box
        className={cx(container, {
          [withLogo]: customerLogo && customerLogos[customer],
        })}
        position="relative"
      >
        <Box flex="auto">
          {allowLoginPassword && (
            <>
              <Typography className="signInTitle" variant="h3">
                {t('signIn.title')}
              </Typography>
            </>
          )}

          {!!errorKey && (
            <>
              <Box mb={3}>
                <Alert severity="error">{t(errorKey)}</Alert>
              </Box>
              {isUnconfirmedAccount && (
                <Box mb={6} display="flex" justifyContent="flex-end">
                  <Typography>
                    <Link
                      to="/confirm-account"
                      component={RouterLink}
                      className={confirmAccountLink}
                    >
                      {t('signIn.confirmAccount')}
                    </Link>
                  </Typography>
                </Box>
              )}
            </>
          )}
          {allowLoginPassword && (
            <>
              <Box mb={3}>
                <FormControl
                  className={formControlContainer}
                  fullWidth
                  required
                >
                  <FormLabel>{t('signIn.email.label')}</FormLabel>
                  <TextField
                    autoComplete="off"
                    type="email"
                    variant="filled"
                    hiddenLabel
                    InputProps={{ disableUnderline: true }}
                    FormHelperTextProps={{ variant: 'standard' }}
                    value={user}
                    onChange={handleUserChanged}
                    error={
                      (userTouched && !user)
                      || (userTouched && user && !userValid)
                    }
                    helperText={
                      (userTouched && !user && t('signUp.email.required'))
                      || (userTouched
                        && user
                        && !userValid
                        && t('signUp.email.validation'))
                    }
                  />
                </FormControl>
              </Box>
              <Box mb={3}>
                <FormControl
                  className={formControlContainer}
                  fullWidth
                  required
                >
                  <FormLabel>{t('signIn.password.label')}</FormLabel>
                  <TextField
                    autoComplete="off"
                    type="password"
                    variant="filled"
                    hiddenLabel
                    InputProps={{ disableUnderline: true }}
                    FormHelperTextProps={{ variant: 'standard' }}
                    value={pass}
                    onChange={handlePassChanged}
                    onKeyPress={handleEnterKeyPress}
                    error={passTouched && !pass}
                    helperText={
                      passTouched && !pass && t('signIn.password.required')
                    }
                  />
                </FormControl>
              </Box>
              <Box display={'flex'} justifyContent={'end'} mt={'6px'} mb={3}>
                <RouterLink
                  className="forgotpasswordLink"
                  to="/forgot-password"
                >
                  {t('signIn.buttons.forgotPassword')}
                </RouterLink>
              </Box>
              <Box mb={3}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={!canLogin}
                  onClick={login}
                  className="signInButton"
                >
                  {t('signIn.buttons.signIn')}
                </Button>
              </Box>
            </>
          )}
          {customerLogo && customerLogos[customer] && (
            <a
              href={customerLinks[customer] || ''}
              target="_blanc"
              className={customerLogoClassName}
            >
              <img src={customerLogos[customer]} alt={`${customer} logo`} />
            </a>
          )}

          <Box mb={3} className="orText">
            <Typography>or</Typography>
          </Box>

          <Box className="provider-signin-container">
            {providers?.map(({ provider, text, iconUrl }) => (
              <Button
                key={provider}
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={() => socialLogin(provider)}
                className={providerSignIn}
              >
                {(iconUrl || loginProvidersLogos[provider]) && (
                  <img
                    src={iconUrl || loginProvidersLogos[provider]}
                    alt="Google G logo"
                    className={providersLogo}
                  />
                )}
                {t(text)}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default React.memo(Login);
